import React, { useState } from 'react';
import { Users, Phone, Circle, ChevronDown, ChevronUp } from 'lucide-react';
import { AgentPresence } from '../../services/support';
import { cn } from '../../utils/helpers';

interface OnlineAgentsIndicatorProps {
  agents: AgentPresence[];
  currentAgentIdentity?: string | null;
}

const OnlineAgentsIndicator: React.FC<OnlineAgentsIndicatorProps> = ({
  agents,
  currentAgentIdentity,
}) => {
  const [expanded, setExpanded] = useState(false);

  const onlineCount = agents.filter((a) => a.status === 'online').length;
  const busyCount = agents.filter((a) => a.status === 'busy').length;
  const totalOnline = onlineCount + busyCount;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
        return 'text-success';
      case 'busy':
        return 'text-warning';
      default:
        return 'text-muted-foreground';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'online':
        return 'Available';
      case 'busy':
        return 'On a call';
      default:
        return 'Offline';
    }
  };

  return (
    <div className="card p-3">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between"
      >
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium text-foreground">
            Online Agents
          </span>
          <span className="ml-1 px-2 py-0.5 bg-primary/10 text-primary text-xs font-semibold rounded-full">
            {totalOnline}
          </span>
        </div>
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        )}
      </button>

      {/* Summary badges */}
      <div className="flex items-center gap-2 mt-2">
        <span className="flex items-center gap-1 text-xs text-muted-foreground">
          <Circle className="h-2 w-2 fill-success text-success" />
          {onlineCount} available
        </span>
        <span className="flex items-center gap-1 text-xs text-muted-foreground">
          <Circle className="h-2 w-2 fill-warning text-warning" />
          {busyCount} on call
        </span>
      </div>

      {/* Expanded agent list */}
      {expanded && agents.length > 0 && (
        <div className="mt-3 pt-3 border-t border-border space-y-2">
          {agents.map((agent) => (
            <div
              key={agent.agentId}
              className={cn(
                'flex items-center justify-between p-2 rounded-lg',
                agent.identity === currentAgentIdentity
                  ? 'bg-primary/10 border border-primary/20'
                  : 'bg-background/50'
              )}
            >
              <div className="flex items-center gap-2">
                <Circle
                  className={cn(
                    'h-2 w-2',
                    getStatusColor(agent.status),
                    agent.status === 'online' || agent.status === 'busy'
                      ? 'fill-current'
                      : ''
                  )}
                />
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {agent.name}
                    {agent.identity === currentAgentIdentity && (
                      <span className="ml-1 text-xs text-primary">(You)</span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {agent.identity}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {agent.status === 'busy' && (
                  <Phone className="h-3 w-3 text-warning animate-pulse" />
                )}
                <span className={cn('text-xs', getStatusColor(agent.status))}>
                  {getStatusLabel(agent.status)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {expanded && agents.length === 0 && (
        <div className="mt-3 pt-3 border-t border-border">
          <p className="text-xs text-muted-foreground text-center">
            No agents online
          </p>
        </div>
      )}
    </div>
  );
};

export default OnlineAgentsIndicator;
